import { useCallback, useState } from 'react';
import shippingService, { RateRequestData, ShippingRate } from '../../services/shippingService';
import { ImportCandidate } from '../../services/orderImportService';

export type RowRateStatus = 'idle' | 'loading' | 'done' | 'error';

export interface RowRateState {
  status: RowRateStatus;
  rates: ShippingRate[];
  error?: string;
}

const MAX_CONCURRENT = 5;

/** Parsed addresses carry the city as a plain string or, once enriched, as `{ name }`. */
function cityName(city: unknown): string {
  if (typeof city === 'string') return city;
  return (city as { name?: string } | null)?.name || '';
}

/** Build a /rates request from a parsed candidate order. */
function buildRateRequest(candidate: ImportCandidate): RateRequestData {
  const origin = candidate.addresses.from?.countryCode || '';
  const destination = candidate.addresses.to?.countryCode || '';
  const parcel = candidate.parcels[0];

  return {
    origin,
    destination,
    originPostalCode: candidate.addresses.from?.zipCode || '',
    destinationPostalCode: candidate.addresses.to?.zipCode || '',
    originCity: cityName(candidate.addresses.from?.city),
    destinationCity: cityName(candidate.addresses.to?.city),
    shippingType: 'package',
    shipmentType: origin === destination ? 'domestic' : 'international',
    parcels: candidate.parcels.map((p) => ({
      quantity: p.quantity,
      weight: p.weight,
      length: p.length,
      width: p.width,
      height: p.height,
      physicalWeight: p.physicalWeight,
      dimensionalWeight: p.dimensionalWeight,
    })),
    totalWeight: parcel?.weight ?? 0,
    unitSystem: 'metric',
    isInternational: origin !== destination,
    compareAllProviders: true,
  };
}

/**
 * Fetches shipping rates for many candidate orders concurrently (max 5 in flight),
 * exposing per-row status so the review table can render each row independently.
 */
export function useBatchRates() {
  const [rowRates, setRowRates] = useState<Record<number, RowRateState>>({});
  const [isFetching, setIsFetching] = useState(false);

  const setRow = useCallback((rowNumber: number, state: RowRateState) => {
    setRowRates((prev) => ({ ...prev, [rowNumber]: state }));
  }, []);

  const fetchAll = useCallback(
    async (candidates: ImportCandidate[], currency?: string) => {
      // Only fetch rates for rows without blocking errors.
      const queue = candidates.filter((c) => c.errors.length === 0);

      // Mark everything as loading up front.
      setRowRates((prev) => {
        const next = { ...prev };
        queue.forEach((c) => {
          next[c.rowNumber] = { status: 'loading', rates: [] };
        });
        return next;
      });

      setIsFetching(true);

      let cursor = 0;
      const worker = async () => {
        while (cursor < queue.length) {
          const candidate = queue[cursor++];
          try {
            const result = await shippingService.getRates(buildRateRequest(candidate), currency);
            const rates = (result?.services || []) as ShippingRate[];
            setRow(candidate.rowNumber, {
              status: rates.length ? 'done' : 'error',
              rates,
              error: rates.length ? undefined : (result?.error_message || 'No rates available'),
            });
          } catch (err: any) {
            setRow(candidate.rowNumber, {
              status: 'error',
              rates: [],
              error: err?.response?.data?.message || err?.message || 'Failed to fetch rates',
            });
          }
        }
      };

      const workers = Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, () => worker());
      await Promise.all(workers);
      setIsFetching(false);
    },
    [setRow]
  );

  const reset = useCallback(() => {
    setRowRates({});
    setIsFetching(false);
  }, []);

  return { rowRates, isFetching, fetchAll, reset };
}

/** Pick the cheapest or fastest rate from a list. */
export function pickRate(rates: ShippingRate[], strategy: 'cheapest' | 'fastest'): ShippingRate | undefined {
  if (!rates.length) return undefined;
  if (strategy === 'cheapest') {
    return rates.reduce((best, r) => (r.price < best.price ? r : best), rates[0]);
  }
  return rates.reduce((best, r) => (r.transit_days < best.transit_days ? r : best), rates[0]);
}
